import { EINSTUFUNG, GEWERK, farbzustand, z } from './gemeinsam';
import {
  ContentSection, DataTable, EmptyState, KpiCard, KpiReihe, StatusBadge,
  type Spalte,
} from '../../muster';

/**
 * Kollisionen und Einstufung: der zu prüfende Bestand und die Einstufung —
 * die entscheidende Zahl. „echt" muss die kleinste Gruppe bleiben.
 */
export function KollisionenUndEinstufung({
  bestand, luecken, k,
}: {
  bestand: any;
  luecken: any[];
  k: any;
}) {
  return (
    <>
      {/* --- Der Bestand ------------------------------------------------ */}
      <ContentSection titel="Zu prüfender Bestand">
        <KpiReihe>
          {(bestand?.jeGewerk ?? []).map((g: any) => (
            <KpiCard
              key={g.gewerk}
              titel={GEWERK[g.gewerk] ?? g.gewerk}
              wert={g.anzahl}
            />
          ))}
          <KpiCard
            titel="Aussparungen"
            wert={bestand?.aussparungen ?? 0}
            fussnote={'Sie entscheiden über „vorgesehen".'}
          />
        </KpiReihe>

        {(luecken ?? []).length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12,
                        borderTop: '1px solid var(--linie)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
              Was in diesen Zahlen NICHT steckt
            </div>

            {(luecken ?? []).map((l: any) => (
              <div key={l.was} style={{ marginBottom: 8 }}>
                <StatusBadge zustand="warnung" text={l.was} klein />
                <div className="schwach" style={{ fontSize: 12, marginLeft: 18 }}>
                  {l.grund}
                </div>
                <div className="schwach" style={{ fontSize: 12, marginLeft: 18 }}>
                  → {l.loesung}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="schwach" style={{ fontSize: 12, marginBottom: 0, marginTop: 12 }}>
          Paarweise wären das {z(bestand?.gesamt ?? 0)} × {z(bestand?.gesamt ?? 0)}
          {' '}Vergleiche. Gesucht wird deshalb nur innerhalb einer Rasterzelle
          von 2 × 2 × 1 m — in Z flacher, weil im Deckenhohlraum alles
          übereinander liegt und dort die Höhe entscheidet.
        </p>
      </ContentSection>

      {/* --- Die Kollisionen, falls es welche gibt ----------------------- */}
      {k && k.gesamt > 0 && (
        <>
          <DataTable
            name="Einstufung — die entscheidende Zahl"
            spalten={einstufungsSpalten(k.gesamt)}
            zeilen={(k.jeEinstufung ?? []) as any[]}
            schluessel={(e: any) => e.einstufung}
            suche={false}
            sortierung={{ spalte: 'anzahl', richtung: 'ab' }}
            ausfuhr
            ausfuhrDateiname="Einstufung"
            leer={(
              <EmptyState
                titel="Keine Einstufung."
                erklaerung="Der Lauf hat Kollisionen gefunden, aber keine eingestuft."
              />
            )}
          />

          <p className="schwach" style={{ fontSize: 12 }}>
            „echt" muss die <strong>kleinste</strong> Gruppe sein. Ist sie die
            größte, greift die Aussparungs- oder Selbstberührungserkennung
            nicht — dann ist die Meldung falsch, nicht das Modell.
          </p>
        </>
      )}
    </>
  );
}

/** Die drei Spalten der Einstufungstabelle — der Anteil braucht die Summe. */
function einstufungsSpalten(gesamt: number): Spalte<any>[] {
  return [
    {
      schluessel: 'einstufung', titel: 'Einstufung', art: 'status',
      immerSichtbar: true, breite: '220px',
      wert: (e) => EINSTUFUNG[e.einstufung]?.text ?? e.einstufung,
      inhalt: (e) => (
        <StatusBadge
          klein
          zustand={farbzustand(EINSTUFUNG[e.einstufung]?.farbe)}
          text={EINSTUFUNG[e.einstufung]?.text ?? e.einstufung}
        />
      ),
    },
    { schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', wert: (e) => e.anzahl },
    {
      schluessel: 'anteil', titel: 'Anteil', art: 'zahl', einheit: '%',
      nachkomma: 0,
      wert: (e) => (gesamt > 0 ? 100 * e.anzahl / gesamt : 0),
    },
  ];
}
